import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { Move, X, Edit3, CornerDownRight } from 'lucide-react'
import { DialogBubbleData } from '../types'

interface DialogBubbleProps {
  bubble: DialogBubbleData
  isSelected: boolean
  onClick: () => void
  onUpdate: (id: string, updates: Partial<DialogBubbleData>) => void
  onDelete: () => void
}

export const DialogBubble: React.FC<DialogBubbleProps> = ({
  bubble,
  isSelected,
  onClick,
  onUpdate,
  onDelete
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(bubble.text)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeStart, setResizeStart] = useState<{x: number, y: number, width: number, height: number} | null>(null)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: bubble.id,
  })

  // 使用ref来跟踪拖拽开始时的位置
  const dragStartPos = useRef({ x: bubble.x, y: bubble.y })
  
  // 拖拽开始时记录初始位置
  useEffect(() => {
    if (isDragging) {
      dragStartPos.current = { x: bubble.x, y: bubble.y }
    }
  }, [isDragging, bubble.x, bubble.y])
  
  const style = {
    position: 'absolute' as const,
    left: bubble.x,
    top: bubble.y,
    width: bubble.width,
    minHeight: bubble.height,
    fontSize: bubble.fontSize,
    fontFamily: bubble.fontFamily,
    color: bubble.color,
    backgroundColor: bubble.type === 'explode' ? 'transparent' : bubble.backgroundColor,
    borderColor: bubble.type === 'explode' ? 'transparent' : bubble.borderColor,
    borderWidth: bubble.type === 'explode' ? 0 : bubble.borderWidth,
    borderRadius: bubble.type === 'explode' ? 0 : bubble.borderRadius,
    padding: bubble.padding,
    zIndex: isSelected ? 60 : 50,
    opacity: isDragging ? 0.8 : 1,
    boxSizing: 'border-box' as const,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition: isDragging ? 'none' : 'all 0.2s ease'
  }

  // Resize逻辑保持不变，但优化性能
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsResizing(true)
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: bubble.width,
      height: bubble.height
    })
    document.body.style.cursor = 'nwse-resize'
  }, [bubble.width, bubble.height])

  useEffect(() => {
    if (!isResizing) return
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStart) return
      const dx = e.clientX - resizeStart.x
      const dy = e.clientY - resizeStart.y
      let newWidth = Math.max(60, resizeStart.width + dx)
      let newHeight = Math.max(32, resizeStart.height + dy)
      onUpdate(bubble.id, { width: newWidth, height: newHeight })
    }
    
    const handleMouseUp = () => {
      setIsResizing(false)
      setResizeStart(null)
      document.body.style.cursor = ''
    }
    
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, resizeStart, bubble.id, onUpdate])

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.select()
    }
  }, [isEditing])

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
  }, [])

  const handleTextSubmit = useCallback(() => {
    onUpdate(bubble.id, { text: editText })
    setIsEditing(false)
  }, [bubble.id, editText, onUpdate])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleTextSubmit()
    }
    if (e.key === 'Escape') {
      setEditText(bubble.text)
      setIsEditing(false)
    }
  }, [handleTextSubmit, bubble.text])

  // 优化点击处理，确保可以正确选中
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onClick()
  }, [onClick])

  const renderBubbleTail = () => {
    if (bubble.type === 'thought') {
      // 思考气泡 - 三个渐小的圆点
      return (
        <div className="absolute -bottom-6 left-6">
          <div className="w-4 h-4 bg-blue-50 rounded-full border-2 border-dashed border-blue-400 absolute"></div>
          <div className="w-3 h-3 bg-blue-50 rounded-full border border-dashed border-blue-400 absolute left-4 top-2"></div>
          <div className="w-2 h-2 bg-blue-50 rounded-full border border-dashed border-blue-400 absolute left-6 top-3"></div>
        </div>
      )
    } else if (bubble.type === 'shout') {
      // 喊叫气泡 - 锯齿状尾巴
      return (
        <div className="absolute -bottom-3 left-6">
          <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[12px] border-l-transparent border-r-transparent border-t-red-600"></div>
          <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-red-50 absolute left-1 top-1"></div>
        </div>
      )
    } else if (bubble.type === 'explode') {
      // 爆炸气泡不渲染箭头
      return null
    } else {
      // 普通对话气泡 - 圆滑尾巴
      if (bubble.arrowDirection === 'up') {
        return (
          <div className="absolute -top-2 left-6">
            <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-b-[12px] border-l-transparent border-r-transparent border-b-gray-800"></div>
            <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-b-[10px] border-l-transparent border-r-transparent border-b-white absolute left-1 top-1"></div>
          </div>
        )
      } else {
        return (
          <div className="absolute -bottom-2 left-6">
            <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-t-[12px] border-l-transparent border-r-transparent border-t-gray-800"></div>
            <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-l-transparent border-r-transparent border-t-white absolute left-1 top-1"></div>
          </div>
        )
      }
    }
  }

  const getBubbleStyle = () => {
    const baseStyle = 'transition-all duration-200 cursor-pointer relative'
    switch (bubble.type) {
      case 'thought':
        return `${baseStyle} rounded-full border-2 border-dashed border-blue-400 bg-blue-50 shadow-lg`
      case 'shout':
        return `${baseStyle} rounded-sm border-4 border-red-600 bg-red-50 shadow-xl transform rotate-1 font-bold`
      case 'explode':
        return `${baseStyle} bg-transparent` // 不加任何shadow/border/borderRadius
      default:
        return `${baseStyle} rounded-2xl border-2 border-gray-800 bg-white shadow-md`
    }
  }

  const getTextStyle = () => {
    switch (bubble.type) {
      case 'thought':
        return 'text-blue-800 italic font-medium'
      case 'shout':
        return 'text-red-900 font-bold uppercase tracking-wide'
      case 'explode':
        return 'text-yellow-900 font-extrabold drop-shadow-lg'
      default:
        return 'text-gray-900'
    }
  }

  // 爆炸气泡SVG背景
  const renderExplodeBg = () => {
    if (bubble.type !== 'explode') return null
    const w = bubble.width
    const h = bubble.height
    // 生成紧贴四边的锯齿顶点
    const points = [
      [0.08*w, h/2], [0,0], [0.25*w,0.18*h], [0.33*w,0], [0.5*w,0.18*h], [0.67*w,0], [0.75*w,0.18*h], [w,0], [0.92*w,h/2], [w,h], [0.75*w,0.82*h], [0.67*w,h], [0.5*w,0.82*h], [0.33*w,h], [0.25*w,0.82*h], [0,h]
    ].map(p=>p.join(",")).join(" ")
    return (
      <svg className="absolute inset-0 w-full h-full z-0 pointer-events-none" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <polygon points={points}
          fill={bubble.backgroundColor || '#fffbe6'}
          stroke={bubble.borderColor || '#facc15'}
          strokeWidth={bubble.borderWidth || 3}
        />
      </svg>
    )
  }

  const renderBubbleEffects = () => {
    if (bubble.type === 'shout') {
      // 喊叫气泡的震动效果线条
      return (
        <>
          <div className="absolute -top-1 -left-1 w-full h-full rounded-sm border-2 border-red-400 opacity-30 animate-pulse"></div>
          <div className="absolute -top-2 -left-2 w-full h-full rounded-sm border border-red-300 opacity-20 animate-ping"></div>
        </>
      )
    }
    return null
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${getBubbleStyle()} ${(isSelected && bubble.type !== 'explode') ? 'ring-2 ring-primary-400 ring-offset-2' : ''}`}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      {...attributes}
    >
      {bubble.type === 'explode' && renderExplodeBg()}
      {/* 气泡特效 */}
      {renderBubbleEffects()}
      {/* 拖拽手柄 */}
      {isSelected && (
        <div className="absolute -top-8 -right-8 flex space-x-1">
          <button
            {...listeners}
            className="w-6 h-6 bg-primary-500 hover:bg-primary-600 text-white rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing"
            title="拖拽移动"
          >
            <Move size={12} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsEditing(true)
            }}
            className="w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center"
            title="编辑文本"
          >
            <Edit3 size={12} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center"
            title="删除"
          >
            <X size={12} />
          </button>
        </div>
      )}
      {/* 文本内容 */}
      {isEditing ? (
        <textarea
          ref={textareaRef}
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={handleTextSubmit}
          onKeyDown={handleKeyDown}
          className={`absolute top-1/2 left-1/2 w-full h-full flex items-center justify-center text-center resize-none border-none outline-none bg-transparent ${getTextStyle()}`}
          style={{ 
            fontSize: bubble.fontSize,
            fontFamily: bubble.fontFamily,
            padding: 0,
            margin: 0,
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1.2
          }}
        />
      ) : (
        <div
          className={`absolute top-1/2 left-1/2 w-full h-full flex items-center justify-center text-center leading-tight ${getTextStyle()}`}
          style={{
            fontSize: bubble.fontSize,
            fontFamily: bubble.fontFamily,
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1.2
          }}
        >
          {bubble.text}
        </div>
      )}
      {/* 右下角resize手柄 */}
      {isSelected && (
        <div
          className="absolute right-0 bottom-0 w-6 h-6 flex items-end justify-end cursor-nwse-resize z-20"
          onMouseDown={handleResizeMouseDown}
          style={{ touchAction: 'none' }}
        >
          <CornerDownRight size={18} className="text-gray-400" />
        </div>
      )}
      {/* 气泡尾巴 */}
      {renderBubbleTail()}
      {/* 序号 */}
      {bubble.sequence && (
        <div className="absolute -top-2 -left-2 w-5 h-5 bg-primary-500 text-white text-xs rounded-full flex items-center justify-center">
          {bubble.sequence}
        </div>
      )}
    </div>
  )
} 